import { useState, useRef, useEffect } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

const HERO_IMG = "https://storage.readdy-site.link/project_files/bac881ec-b9cc-4710-87b9-9f51b6eb607d/e0dd4f69-375b-4702-b128-107eedb0f7b2_Exterior-50.jpg?v=c6e25c9414142f3154f4b33806d75810";

const CONTACT_INFO = [
  { icon: "ri-mail-line", label: "Email Us", value: "info@Havinar.com", href: "mailto:info@Havinar.com" },
];

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function ContactHero() {
  return (
    <section className="relative h-[55vh] min-h-[420px] overflow-hidden">
      <img src={HERO_IMG} alt="Contact hero" className="absolute inset-0 w-full h-full object-cover object-top" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
        <p className="text-amber-400 text-xs font-semibold tracking-[0.35em] uppercase mb-4 opacity-0 animate-[fadeInUp_0.8s_0.3s_forwards]">
          Let&apos;s Connect
        </p>
        <h1 className="text-white font-serif text-6xl md:text-7xl font-bold mb-6 opacity-0 animate-[fadeInUp_0.8s_0.5s_forwards]">
          Contact Us
        </h1>
        <p className="text-white/80 text-lg max-w-lg leading-relaxed opacity-0 animate-[fadeInUp_0.8s_0.7s_forwards]">
          Interested in our homes or want to learn more about what we&apos;re building? We&apos;d love to connect.
        </p>
      </div>
    </section>
  );
}

type FormStatus = "idle" | "submitting" | "success" | "error";

function ContactForm() {
  const { ref, visible } = useInView();
  const [status, setStatus] = useState<FormStatus>("idle");
  const [charCount, setCharCount] = useState(0);
  const MAX_CHARS = 500;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement)?.value || "";
    if (message.length > MAX_CHARS) return;

    setStatus("submitting");
    const formData = new FormData(form);
    const body = new URLSearchParams();
    formData.forEach((val, key) => body.append(key, val as string));

    try {
      const res = await fetch("https://readdy.ai/api/form/d6u1kqv6t0doov8ffmg0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section ref={ref} className="bg-white py-28 px-8">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-16 items-start">
        {/* Left info */}
        <div className={`lg:col-span-2 transition-all duration-1000 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}>
          <p className="text-amber-700 text-xs font-semibold tracking-[0.3em] uppercase mb-5">Get in Touch</p>
          <h2 className="text-4xl font-bold font-serif text-stone-900 leading-tight mb-8">
            We&apos;re Ready<br /><em className="font-light">When You Are</em>
          </h2>
          <p className="text-stone-500 text-sm leading-relaxed mb-12">
            If you&apos;re interested in an available or upcoming Havinar Homes property, want to learn more about our portfolio, or simply want to get in touch — we&apos;re here.
          </p>
          <div className="flex flex-col gap-8">
            {CONTACT_INFO.map((c) => (
              <div key={c.label} className="flex items-start gap-5">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-amber-50 shrink-0">
                  <i className={`${c.icon} text-xl text-amber-700`} />
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-widest uppercase text-stone-400 mb-1">{c.label}</p>
                  <a href={c.href} className="text-stone-800 font-medium hover:text-amber-700 transition-colors cursor-pointer">
                    {c.value}
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Social */}
          <div className="mt-12 pt-8 border-t border-stone-100">
            <p className="text-xs font-semibold tracking-widest uppercase text-stone-400 mb-5">Follow Us</p>
            <div className="flex gap-3">
              {[
                { icon: "ri-instagram-line", href: "#" },
                { icon: "ri-facebook-line", href: "#" },
                { icon: "ri-linkedin-line", href: "#" },
              ].map((s) => (
                <a
                  key={s.icon}
                  href={s.href}
                  rel="nofollow"
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-stone-200 text-stone-500 hover:border-amber-600 hover:text-amber-600 transition-all duration-300 cursor-pointer"
                >
                  <i className={`${s.icon} text-lg`} />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Form */}
        <div className={`lg:col-span-3 bg-stone-50 rounded-2xl p-10 border border-stone-100 transition-all duration-1000 delay-200 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}>
          {status === "success" ? (
            <div className="flex flex-col items-center justify-center text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-amber-100 mb-6">
                <i className="ri-check-line text-3xl text-amber-700" />
              </div>
              <h3 className="text-2xl font-bold font-serif text-stone-900 mb-3">Message Sent!</h3>
              <p className="text-stone-500 text-sm max-w-xs">
                Thank you for reaching out. We&apos;ll be in touch within 24 hours.
              </p>
              <button
                onClick={() => setStatus("idle")}
                className="whitespace-nowrap mt-8 px-8 py-3 bg-stone-900 text-white text-sm font-semibold tracking-widest uppercase rounded-full hover:bg-amber-700 transition-all duration-300 cursor-pointer"
              >
                Send Another
              </button>
            </div>
          ) : (
            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs font-semibold tracking-widest uppercase text-stone-500 mb-2 block">
                    First Name <span className="text-amber-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    required
                    placeholder="Jane"
                    className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:border-amber-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold tracking-widest uppercase text-stone-500 mb-2 block">
                    Last Name <span className="text-amber-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    required
                    placeholder="Smith"
                    className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:border-amber-500 transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold tracking-widest uppercase text-stone-500 mb-2 block">
                  Email Address <span className="text-amber-600">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="jane@example.com"
                  className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:border-amber-500 transition-colors"
                />
              </div>
              <div>
                <label className="text-xs font-semibold tracking-widest uppercase text-stone-500 mb-2 block">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="(555) 000-0000"
                  className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:border-amber-500 transition-colors"
                />
              </div>
              <div>
                <label className="text-xs font-semibold tracking-widest uppercase text-stone-500 mb-2 block">
                  I&apos;m Interested In
                </label>
                <select
                  name="interest"
                  className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 focus:outline-none focus:border-amber-500 transition-colors cursor-pointer"
                >
                  <option value="">Select an option</option>
                  <option value="Available Homes">Available Homes</option>
                  <option value="Upcoming Projects">Upcoming Projects</option>
                  <option value="Portfolio Inquiry">Portfolio Inquiry</option>
                  <option value="General Inquiry">General Inquiry</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold tracking-widest uppercase text-stone-500 mb-2 block">
                  Message <span className="text-amber-600">*</span>
                </label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  maxLength={MAX_CHARS}
                  placeholder="Tell us about your project or inquiry..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:border-amber-500 transition-colors resize-none"
                />
                <p className={`text-xs mt-1 text-right ${charCount > MAX_CHARS ? "text-red-500" : "text-stone-400"}`}>
                  {charCount}/{MAX_CHARS}
                </p>
              </div>
              {status === "error" && (
                <p className="text-red-500 text-sm text-center">Something went wrong. Please try again.</p>
              )}
              <button
                type="submit"
                disabled={status === "submitting" || charCount > MAX_CHARS}
                className="whitespace-nowrap w-full py-4 bg-stone-900 hover:bg-amber-700 disabled:bg-stone-400 text-white text-sm font-semibold tracking-widest uppercase rounded-full transition-all duration-300 cursor-pointer"
              >
                {status === "submitting" ? "Sending..." : "Send Message"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

export default function ContactPage() {
  return (
    <main>
      <Navbar />
      <ContactHero />
      <ContactForm />
      <Footer />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Inter:wght@300;400;500;600&display=swap');
        body { font-family: 'Inter', sans-serif; }
        .font-serif { font-family: 'Playfair Display', Georgia, serif; }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </main>
  );
}
